import { useRef, useState } from "react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import { LoadingButton } from "../../../../../components/button/Button";
import Condition from "../../../../../components/condition/Condition";
import { useGenerateArrayFromObj } from "../../../../../components/input/hook/useGenerateOptions2";
import ProductWithPriceInvoice from "../../../../../components/product/ProductWithPrice";
import { useToast } from "../../../../../context/toast/ToastContext";
import { MainContainerForm } from "../../../../../styles/Main.styles";
import { useGetInvoiceConstant } from "../../../api/invoice/constant/resolver";
import GrosirInput from "../../__component/GrosirInput";
import PaymentMethodInput from "../../__component/PaymentMethodInput";
import RetailInput from "../../__component/RetailInput";
import Input from "../../../../../components/input/Input-v2";
import { ModalFC } from "../../../../../components/modal/Modal";
import { useAddInvoice } from "../../../api/invoice/resolver";
import { FromStringToDate } from "../../../../../utils/Helper";

export interface Form_Create_Invoice {
  invoice_date: string;
  invoice_type: string;
  payment_method_id: string;
  invoice_status: string;
  product_list: any;
  customer_name: string;
  customer_address?: string;
  customer_phone_number?: string;
  sub_dealer_id?: string;
  payment_due_date?: string;
  description?: string;
}

export default function FormAdd() {
  const { show } = useToast();
  const { control, handleSubmit, setValue, watch, getValues, trigger } =
    useForm<Form_Create_Invoice>({
      defaultValues: {
        customer_name: "",
      },
    });
  const { data: dataRawInvoiceConstant, isLoading: fetchInvoiceConstant } =
    useGetInvoiceConstant();
  const dataInvoiceConstant = dataRawInvoiceConstant?.data.data;
  const { data: dataInvoiceType } = useGenerateArrayFromObj({
    dataSource: dataInvoiceConstant?.type,
  });
  const { data: dataInvoiceStatus } = useGenerateArrayFromObj({
    dataSource: dataInvoiceConstant?.status,
  });
  const { mutate: mutateAddInvoice, isLoading: loadingAddInvoice } =
    useAddInvoice();
  const refForm = useRef<any>();
  const [showModal, setShowModal] = useState(false);
  const isFetching = fetchInvoiceConstant;

  const handleFormSubmit: SubmitHandler<Form_Create_Invoice> = (data) => {
    const currentDate = new Date().toISOString();
    mutateAddInvoice({
      customer_name: data.customer_name,
      invoice_date: FromStringToDate(currentDate, true),
      invoice_status: data.invoice_status,
      invoice_type: data.invoice_type,
      payment_method_id: data.payment_method_id,
      product_list: JSON.stringify(data.product_list), // Parse as BE required data to be string
      customer_address: data.customer_address,
      customer_phone_number: data.customer_phone_number,
      payment_due_date: data.payment_due_date,
      sub_dealer_id: data.sub_dealer_id,
      description: data.description,
    });
  };

  const handleOpenPaymentConfimation = () => {
    const isBelumLunas =
      getValues("invoice_status") ===
      dataInvoiceConstant?.status["Belum Lunas"];
    if (isBelumLunas && getValues("payment_method_id") === null) {
      show("Mohon memilih pembayaran", "warning");
      return;
    }
    refForm.current.requestSubmit();
  };

  return (
    <form ref={refForm} onSubmit={(e) => handleSubmit(handleFormSubmit)(e)}>
      <MainContainerForm mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Input.Controller
              control={control}
              name="invoice_type"
              rules={{ required: "Kolom ini tidak boleh kosong" }}
              render={(field, fieldState) => (
                <Input.Select
                  isFetching={isFetching}
                  label="Tipe Invoice"
                  options={dataInvoiceType || []}
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input.Controller
              control={control}
              name="invoice_status"
              rules={{ required: "Kolom ini tidak boleh kosong" }}
              render={(field, fieldState) => (
                <Input.Select
                  isFetching={isFetching}
                  label="Status Invoice"
                  options={
                    dataInvoiceStatus
                      ? dataInvoiceStatus.filter((item) => item.value > 0)
                      : []
                  }
                  value={field.value}
                  onChange={(e) => field.onChange(e.value)}
                  error={fieldState.error?.message}
                />
              )}
            />
          </Grid>
          <Condition.IF
            condition={
              watch("invoice_status") !== undefined &&
              watch("invoice_status") ===
                dataInvoiceConstant?.status["Belum Lunas"]
            }
          >
            <Grid item xs={12} md={12}>
              <Input.Controller
                control={control}
                name="payment_due_date"
                render={(field) => (
                  <Input.Date
                    isFetching={isFetching}
                    label="Tanggal Jatuh Tempo"
                    value={field.value}
                    onChange={(e) => field.onChange(e)}
                  />
                )}
              />
            </Grid>
          </Condition.IF>
          <Condition.IF
            condition={
              watch("invoice_type") !== undefined &&
              watch("invoice_type") === dataInvoiceConstant?.type.Grosir
            }
          >
            <GrosirInput control={control} />
          </Condition.IF>
          <Condition.IF
            condition={
              watch("invoice_type") !== undefined &&
              watch("invoice_type") === dataInvoiceConstant?.type.Retail
            }
          >
            <RetailInput control={control} isFetching={isFetching} />
          </Condition.IF>
          <Grid item xs={12}>
            <Input.Controller
              control={control}
              name="description"
              render={(field) => (
                <Input.TextArea
                  isFetching={isFetching}
                  label="Catatan"
                  value={field.value}
                  onChange={(e) => field.onChange(e)}
                />
              )}
            />
          </Grid>
        </Grid>
      </MainContainerForm>
      <MainContainerForm>
        <ProductWithPriceInvoice
          invoiceType={watch("invoice_type")}
          onChange={(e) => setValue("product_list", e)}
        />
        <Box display={"flex"} justifyContent={"flex-end"} mt={2} gap={2}>
          <LoadingButton
            isLoading={false}
            text={"Buat"}
            onClick={async () => {
              const isValid = await trigger([
                "customer_name",
                "invoice_status",
                "invoice_type",
                "product_list",
              ]);
              if (isValid) {
                setShowModal(true);
              }
            }}
          />
        </Box>
      </MainContainerForm>
      <ModalFC open={showModal} onClose={() => setShowModal(false)}>
        <Box minHeight={150}>
          <Typography variant="h6" mb={1}>
            Apakah anda yakin ?
          </Typography>
          {watch("invoice_status") === dataInvoiceConstant?.status.Lunas && (
            <PaymentMethodInput
              control={control}
              isPaymentForm={true}
              dataInvoiceConstant={dataInvoiceConstant}
              invoice_status={watch("invoice_status")}
            />
          )}
          <Box display={"flex"} justifyContent={"flex-end"} mt={2} gap={2}>
            <Button variant="outlined" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <LoadingButton
              onClick={() => handleOpenPaymentConfimation()}
              isLoading={loadingAddInvoice}
              text={"Konfirmasi"}
            />
          </Box>
        </Box>
      </ModalFC>
    </form>
  );
}
